import type { ControlTaskControlGroup, EarthingMeasurementsByNode, PanelControlDraftAnswers, ControlGroupAnswers } from '../services/controlTaskService';

export type PanelNode = { id?: number; type?: string; parentId?: number | null };
export const isPanelControlNode = (node: PanelNode): boolean => node.type?.toUpperCase() === 'PANEL_CONTROL';

export const panelSectionsByNode = (nodes: PanelNode[]): Map<number, number> => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const result = new Map<number, number>();
  nodes.forEach((node) => {
    const visited = new Set<number>();
    let parent: PanelNode | undefined = node;
    while (parent?.id != null && !visited.has(parent.id)) {
      visited.add(parent.id);
      if (isPanelControlNode(parent)) {
        if (node.id != null) result.set(node.id, parent.id);
        break;
      }
      parent = parent.parentId != null ? byId.get(parent.parentId) : undefined;
    }
  });
  return result;
};

export const isCurrentPanelGroup = (
  group: PanelControlViewGroup,
  measurements: EarthingMeasurementsByNode,
  correction = false,
): boolean => {
  if (group.archived) return false;
  if (correction || !Object.prototype.hasOwnProperty.call(measurements, String(group.sourceReportNodeId))) return true;
  return measurements[String(group.sourceReportNodeId)].some((row) => (group.sourceEarthingClientKey ? row.clientKey === group.sourceEarthingClientKey : row.id === group.sourceEarthingMeasurementId)
    && ['MAIN_PANEL', 'SUB_PANEL', 'FIRE_PANEL'].includes(row.measurementPointGroup ?? ''));
};

export const panelQuestionKey = (groupId: number | string, questionKey: string): string => `panel:${groupId}:${questionKey}`;


export type PanelControlViewGroup = Omit<ControlTaskControlGroup, 'id' | 'sourceEarthingMeasurementId'> & {
  id: number | string;
  sourceEarthingMeasurementId?: number;
  sourceEarthingClientKey?: string;
};

const panelLabels: Record<string, string> = {
  MAIN_PANEL: 'Ana Pano', SUB_PANEL: 'Tali Pano', FIRE_PANEL: 'Yangın Panosu',
};

/** Derive from the current form, including unsaved rows; never invent a server group ID. */
export const derivePanelControlGroups = (
  nodes: PanelNode[], saved: ControlTaskControlGroup[], measurements: EarthingMeasurementsByNode,
  correction = false,
): PanelControlViewGroup[] => {
  if (correction) return saved;
  const result: PanelControlViewGroup[] = saved.map((group) => ({ ...group }));
  nodes.filter(isPanelControlNode).forEach((node) => {
    if (node.id == null) return;
    Object.entries(measurements).forEach(([sourceNodeId, rows]) => {
      rows.forEach((row, index) => {
        if (!row.measurementPointGroup || !panelLabels[row.measurementPointGroup]) return;
        if (row.id == null && !row.clientKey) return;
        const existing = result.find((group) => group.reportNodeId === Number(node.id)
          && group.sourceReportNodeId === Number(sourceNodeId)
          && row.id != null && group.sourceEarthingMeasurementId === row.id);
        const description = row.measurementPointDescription ?? '';
        const fields = {
          archived: false, measurementPointGroup: row.measurementPointGroup,
          title: `${panelLabels[row.measurementPointGroup]}${description ? ` - ${description}` : ''}`,
          description, sortOrder: index + 1,
        };
        if (existing) {
          Object.assign(existing, fields);
        } else {
          result.push({
            ...fields,
            id: `draft:${node.id}:${sourceNodeId}:${row.id != null ? `id:${row.id}` : `client:${row.clientKey}`}`,
            reportNodeId: Number(node.id), sourceReportNodeId: Number(sourceNodeId),
            sourceEarthingMeasurementId: row.id,
            sourceEarthingClientKey: row.id == null ? row.clientKey : undefined,
            answers: {},
          });
        }
      });
    });
  });
  return result.sort((a, b) => a.reportNodeId - b.reportNodeId
    || a.sourceReportNodeId - b.sourceReportNodeId || a.sortOrder - b.sortOrder);
};

export const splitPanelAnswersForSubmit = (
  groups: PanelControlViewGroup[], answers: ControlGroupAnswers,
): { controlGroupAnswers?: ControlGroupAnswers; panelControlDraftAnswers?: PanelControlDraftAnswers[] } => {
  if (!groups.length) return {};
  const controlGroupAnswers: ControlGroupAnswers = {};
  const panelControlDraftAnswers: PanelControlDraftAnswers[] = [];
  groups.forEach((group) => {
    const values = answers[String(group.id)] ?? {};
    if (typeof group.id === 'number') controlGroupAnswers[String(group.id)] = values;
    else panelControlDraftAnswers.push({
      reportNodeId: group.reportNodeId, sourceReportNodeId: group.sourceReportNodeId,
      sourceEarthingMeasurementId: group.sourceEarthingMeasurementId,
      sourceEarthingClientKey: group.sourceEarthingClientKey, answers: values,
    });
  });
  return { controlGroupAnswers, panelControlDraftAnswers };
};

/** Remap sources immediately after publish, even if the follow-up GET fails. */
export const applyEarthingSourceIds = (
  measurements: EarthingMeasurementsByNode, ids: Record<string, number> = {},
): EarthingMeasurementsByNode => Object.fromEntries(Object.entries(measurements).map(([node, rows]) => [
  node, rows.map((row) => row.id == null && row.clientKey && ids[row.clientKey] != null
    ? { ...row, id: ids[row.clientKey], clientKey: undefined } : row),
]));

export const reconcilePanelAnswers = (
  previous: PanelControlViewGroup[], answers: ControlGroupAnswers, next: PanelControlViewGroup[],
  ids: Record<string, number> = {},
): ControlGroupAnswers => Object.fromEntries(next.map((group) => {
  const source = previous.find((old) => old.reportNodeId === group.reportNodeId
    && old.sourceReportNodeId === group.sourceReportNodeId
    && (old.id === group.id || (old.sourceEarthingClientKey
      ? ids[old.sourceEarthingClientKey] != null && ids[old.sourceEarthingClientKey] === group.sourceEarthingMeasurementId
      : old.sourceEarthingMeasurementId != null && old.sourceEarthingMeasurementId === group.sourceEarthingMeasurementId)));
  return [String(group.id), source ? answers[String(source.id)] ?? group.answers : group.answers];
}));

export const remapPanelScopedValues = <T>(
  values: Record<string, T>, previous: PanelControlViewGroup[], next: PanelControlViewGroup[],
): Record<string, T> => {
  const changes = previous.flatMap((old) => {
    const current = next.find((group) => old.sourceEarthingMeasurementId != null
      && group.sourceEarthingMeasurementId === old.sourceEarthingMeasurementId
      && group.sourceReportNodeId === old.sourceReportNodeId && group.reportNodeId === old.reportNodeId);
    return current && current.id !== old.id ? [[`panel:${old.id}:`, `panel:${current.id}:`]] : [];
  });
  return Object.fromEntries(Object.entries(values).map(([key, value]) => {
    const change = changes.find(([prefix]) => key.startsWith(prefix));
    return [change ? `${change[1]}${key.slice(change[0].length)}` : key, value];
  }));
};
